/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { FederatedPointerEvent } from 'pixi.js';
import { Container, Graphics, Rectangle, Text } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { BoardModel } from '../game/boardMVC/models/board/BoardModel';
import { EditorBoardView } from './board/EditorBoardView';
import type { EditorTrayModel } from './tray/EditorTrayModel';
import type { TrayViewSettings } from '../game/boardMVC/views/tray/TrayView';
import { EditorTrayView } from './tray/EditorTrayView';
import { TweenManager } from '../libs/TweenManager/TweenManager';
import { globalToGridPos } from '../game/boardMVC/utils/globalToGridPos';
import { registerResizableObject } from '../utils/pixi/registerResizableObject';
import { TILE_WIDTH } from '../game/boardMVC/views/board/TileView';
import { EditMode } from './types';
import type { App } from '../app/App';
import type { TileStackModel } from '../game/boardMVC/models/board/TileStackModel';
import { EditorBoardViewFactory } from './board/EditorBoardViewFactory';
import { copyToClipboard } from '../app/utils/copyToClipboard';

interface EditorAreaViewConfig {
  app: App;
  boardModel: BoardModel;
  trayModel: EditorTrayModel;
  trayViewSettings: TrayViewSettings;
}

export class EditorGameAreaView extends Container {
  private readonly background = new Graphics().rect(0, 0, 800, 800).fill({ color: 0xaaaaaa }).props({
    parent: this,
  });

  public readonly boardView: EditorBoardView;

  public readonly ownTweens = new TweenManager();

  public readonly viewFactory = new EditorBoardViewFactory();

  public readonly trayView: EditorTrayView;

  private selectedStackId: string | null = null;

  public readonly onGridPointerDown = new Signal<(x: number, y: number) => void>();

  private readonly currentWinRateText = new Text('---').props({
    x: 50,
    y: 80,
    parent: this,
  });

  private readonly hash = new Text('---').props({
    x: 50,
    y: 50,
    parent: this,
  });

  constructor({ app, boardModel, trayModel }: EditorAreaViewConfig) {
    super();

    this.boardView = new EditorBoardView(boardModel, this.viewFactory).props({
      parent: this,
    });

    this.trayView = new EditorTrayView(app, trayModel).props({
      parent: this,
    });

    this.boardView.interactiveChildren = false;
    this.interactive = true;
    this.hitArea = new Rectangle(-100000, -100000, 200000, 200000);

    this.onpointerdown = (e: FederatedPointerEvent): void => {
      const { x, y } = globalToGridPos(this.boardView, e.global);

      const stackWithinGrid = x >= 0 && x < boardModel.cols && y >= 0 && y < boardModel.rows;
      if (stackWithinGrid) this.onGridPointerDown.emit(x, y);
    };

    this.onglobalpointermove = (e: FederatedPointerEvent): void => {
      const { x, y } = globalToGridPos(this.boardView, e.global);
      const stackWithinGrid = x >= 0 && x < boardModel.cols && y >= 0 && y < boardModel.rows;
      this.hoveredGridPos = stackWithinGrid ? { x, y } : null;
    };

    this.hash.eventMode = 'static';
    this.hash.cursor = 'pointer';
    this.hash.on('pointertap', () => {
      void copyToClipboard(this.hash.text);
    });

    registerResizableObject(this);
  }

  public displayWinRate(winRate: string): void {
    this.currentWinRateText.text = winRate;
  }

  public updateHash(hash: string): void {
    this.hash.text = hash;
  }

  public refreshEditMode(editorMode: EditMode): void {
    this.trayView.stacksInteractive = [EditMode.EditTiles, EditMode.EditBlockerConnections].includes(editorMode);
    this.interactive = editorMode !== EditMode.Default && editorMode !== EditMode.Remix;
  }

  public refreshBlockerView(stackModel: TileStackModel): void {
    try {
      const { viewFactory } = this;
      const stackView = viewFactory.getStackView(stackModel.id)!;
      const existingBlockerView = stackModel.blocker?.id ? viewFactory.getBlockerView(stackModel.blocker.id) : null;
      existingBlockerView?.destroy({ children: true });
      const blockerView = viewFactory.createBlockerView(stackModel, stackModel.blocker!);
      stackView.addBlockerView(blockerView);
    } catch (e) {
      console.error(`Could not refresh the BlockerView for stack at ${stackModel.x},${stackModel.y}`, e);
    }
  }

  public refreshUnderTileBlockerView(stackModel: TileStackModel): void {
    try {
      const { viewFactory } = this;
      const stackView = viewFactory.getStackView(stackModel.id)!;
      const existingBlockerView = stackModel.underTileBlocker?.id
        ? viewFactory.getBlockerView(stackModel.underTileBlocker.id)
        : null;
      existingBlockerView?.destroy({ children: true });
      const blockerView = viewFactory.createBlockerView(stackModel, stackModel.underTileBlocker!);
      stackView.addBlockerView(blockerView);
    } catch (e) {
      console.error(`Could not refresh the BlockerView for stack at ${stackModel.x},${stackModel.y}`, e);
    }
  }

  public setStackHighlight(stackId: string | null): void {
    const { boardView, selectedStackId: prevStackId } = this;
    const prevHighlight = prevStackId ? boardView.highlightLayer.getHighlight(prevStackId) : null;
    prevHighlight?.hide();

    if (!stackId) {
      this.selectedStackId = null;
      return;
    }

    const newHighlight = boardView.highlightLayer.getHighlight(stackId);
    newHighlight?.show();
    this.selectedStackId = stackId;
  }

  private hoveredGridPos: { x: number; y: number } | null = null;

  public getHoveredGridPos(): { x: number; y: number } | null {
    return this.hoveredGridPos;
  }

  public update(deltaTime: number): void {
    this.ownTweens.update(deltaTime * (1000 / 60));
  }

  private alignBoard(width: number, height: number): void {
    const { boardView } = this;
    const maxScale = 1;
    const verticalAnchorRatio = 0.45;
    const maxWidth = width * 0.95;
    const maxHeight = height * 0.75;

    boardView.props({
      scale: 1,
      x: 0,
      y: 0,
    });

    const bounds = boardView.initialBounds;
    const scaleX = maxWidth / bounds.width;
    const scaleY = maxHeight / bounds.height;
    const scale = Math.min(scaleX, scaleY, maxScale);

    const globalBounds = bounds.clone();
    globalBounds.x = boardView.x + bounds.minX;
    globalBounds.y = boardView.y + bounds.minY;
    globalBounds.scale(scale, scale);

    boardView.props({
      x: width / 2 - globalBounds.width / 2 + (width - maxWidth) / 2 - globalBounds.minX - (width - maxWidth) / 2,
      y: height * verticalAnchorRatio - globalBounds.height / 2 - globalBounds.minY,
      scale,
    });
  }

  public resize(width: number, height: number): void {
    this.background.props({
      width,
      height,
    });

    this.alignBoard(width, height);
    // FIXME: alignment was off a bit, not sure what changed.
    this.boardView.x -= this.boardView.scaleX * (TILE_WIDTH / 2);
    this.currentWinRateText.x = this.boardView.x - 120;
    this.hash.x = this.boardView.x - 120;

    this.trayView.y = height - 85;
  }

  public destroy(): void {
    this.boardView.getModel().destroy();
    this.onGridPointerDown.disconnectAll();
    super.destroy({ children: true });
  }
}
